import React, { useState, useEffect, useRef } from 'react';
import Form from 'react-bootstrap/Form';
import CustomSelect from '../../../profile/components/customSelect';
import { Option } from '../../../profile/components/filterOptions';
import { Search } from 'lucide-react';
import { FILTER_ALL } from '../utils/constants';
import { getScopedFunctions, getScopedKpis, filterSetHas } from '../utils/helpers';

const ALL_FILTER_OPTION = { label: 'All', value: '*' };

const selectMenuStyles = {
    menu: (provided) => ({
        ...provided,
        width: '200%',
        minWidth: '400px',
        zIndex: 9999
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? '#e8efff' : provided.backgroundColor,
        color: '#252b37',
        ':active': {
            ...provided[':active'],
            backgroundColor: state.isSelected ? '#dbe7ff' : '#f2f4f7'
        }
    })
};

const selectedCountPlaceholder = (defaultText, selectedOptions) => (
    selectedOptions.length > 0 ? `${selectedOptions.length} selected` : defaultText
);

const withAllOptionWhenFullySelected = (selectedOptions, allOptions) => (
    allOptions.length > 0 && selectedOptions.length === allOptions.length
        ? [ALL_FILTER_OPTION, ...selectedOptions]
        : selectedOptions
);

const handleMultiFilterChange = (selected, allOptions, onChange) => {
    if (!selected || selected.length === 0) {
        onChange([]);
        return;
    }
    const hasAll = selected.some((item) => item.value === '*');
    if (hasAll || (allOptions.length > 0 && selected.length === allOptions.length)) {
        onChange(allOptions.map((item) => item.value));
        return;
    }
    onChange(selected.map((item) => item.value));
};

const DomainMappingSearchBar = ({
    searchVal = '',
    onSearchChange,
    businesses = [],
    functions = [],
    kpis = [],
    filterBusinesses = new Set(),
    onBusinessFilterChange,
    filterFunctions = new Set(),
    onFunctionFilterChange,
    filterKpis = new Set(),
    onKpiFilterChange,
    filterStatus = FILTER_ALL,
    onStatusFilterChange,
    clearAllFilters,
    matchingCount = 0
}) => {
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [inputVal, setInputVal] = useState(searchVal);
    const containerRef = useRef(null);
    const debounceRef = useRef(null);

    useEffect(() => {
        setInputVal(searchVal);
    }, [searchVal]);

    useEffect(() => () => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
    }, []);

    const handleInputChange = (e) => {
        const val = e.target.value;
        setInputVal(val);
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            onSearchChange(val);
            debounceRef.current = null;
        }, 300);
    };

    const handleClearSearch = () => {
        setInputVal('');
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        onSearchChange('');
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!isFilterOpen) return;

            if (containerRef.current && containerRef.current.contains(event.target)) {
                return;
            }

            if (event.target.closest && event.target.closest('[class*="select-control"]')) {
                return;
            }

            setIsFilterOpen(false);
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isFilterOpen]);

    const bizOptions = businesses.map((b) => ({ value: b.id, label: b.name }));
    const scopedFunctions = getScopedFunctions(functions, filterBusinesses, () => true);
    const fnOptions = scopedFunctions.map((f) => ({ value: f.id, label: f.name }));
    const scopedKpis = getScopedKpis(kpis, functions, filterBusinesses, filterFunctions, () => true);
    const kpiOptions = scopedKpis.map((k) => ({ value: k.id, label: k.name }));

    const selectedBizOptions = bizOptions.filter((b) => filterSetHas(filterBusinesses, b.value));
    const selectedFnOptions = fnOptions.filter((f) => filterSetHas(filterFunctions, f.value));
    const selectedKpiOptions = kpiOptions.filter((k) => filterSetHas(filterKpis, k.value));

    const activeFilterCount = (filterBusinesses.size > 0 ? 1 : 0)
        + (filterFunctions.size > 0 ? 1 : 0)
        + (filterKpis.size > 0 ? 1 : 0)
        + (filterStatus !== FILTER_ALL ? 1 : 0);

    return (
        <div ref={containerRef} className="qd_search-container tw-w-full !tw-p-0">
            <div className={`qd_search-box autocomplete !tw-shadow-none tw-border tw-border-solid tw-border-[#D5D7DA] dark:tw-border-white/10 before:!tw-content-none !tw-h-[36px] ${inputVal && inputVal.trim() !== "" ? "has-btn-close" : ""}`}>
                <span className="tw-absolute tw-left-3 tw-top-1/2 tw-translate-y-[-50%] tw-text-gray-400">
                    <Search size={16} />
                </span>
                <input
                    type="text"
                    className="form-control !tw-py-[2px] !tw-pl-[38px] !tw-pr-[160px]"
                    placeholder="Search for any thing! Just start typing..."
                    value={inputVal}
                    onChange={handleInputChange}
                />

                {inputVal && inputVal.trim() !== "" && (
                    <button
                        type="button"
                        className="btn btn-clear !tw-right-[120px]"
                        title="Clear"
                        onClick={handleClearSearch}
                    >
                        <i className="icon-close"></i>
                    </button>
                )}

                <div className="qd_search-bar-divider !tw-right-[100px] before:!tw-h-[24px] before:!tw-top-[3px]">
                    <span className='!tw-w-4 !tw-h-4'>or</span>
                </div>

                <div className="qd-filtered_button !tw-right-[3px]">
                    <button
                        type="button"
                        id="btn-filter"
                        onClick={() => setIsFilterOpen(prev => !prev)}
                        className={`btn btn-light-secondary tw-flex tw-items-center tw-justify-center !tw-w-[auto] !tw-h-[30px] !tw-pl-[10px] !tw-pr-3 !tw-py-[2px] ${isFilterOpen || activeFilterCount > 0 ? "active" : ""}`}
                    >
                        <i id="filtericon" className='tw-flex tw-items-center tw-justify-center tw-mr-1'>
                            <svg className='!tw-mr-0 tw-d-block' xmlns="http://www.w3.org/2000/svg" fill="#0021a5" height="20" viewBox="0 0 24 24" width="20">
                                <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"></path>
                                <path d="M0 0h24v24H0z" fill="none"></path>
                            </svg>
                        </i>
                        {activeFilterCount > 0 && (
                            <span className="filter-count">{activeFilterCount}</span>
                        )}
                        <span>Filters</span>
                    </button>
                </div>
            </div>

            <div className={isFilterOpen ? "qd_search-filter-rst d-none d-block" : "qd_search-filter-rst d-none"}>
                <div className="qd_search-filter-form">
                    <Form className="form-label-right-md">
                        <h4 className="qd_autosuggest-title">Advanced search</h4>
                        <button onClick={() => setIsFilterOpen(false)} type="button" className="btn btn-link">
                            Close
                        </button>

                        <div className="row row-sm">
                            <Form.Group controlId="business" className="col-6">
                                <Form.Label>Business :</Form.Label>
                                <CustomSelect
                                    isMulti
                                    name="business"
                                    id="business"
                                    options={bizOptions}
                                    onChange={(selected) => handleMultiFilterChange(selected, bizOptions, onBusinessFilterChange)}
                                    placeholder={selectedCountPlaceholder('Select Business', selectedBizOptions)}
                                    classNamePrefix="select-control"
                                    components={{ Option }}
                                    allowSelectAll={true}
                                    allOption={ALL_FILTER_OPTION}
                                    value={withAllOptionWhenFullySelected(selectedBizOptions, bizOptions)}
                                    isClearable={false}
                                    controlShouldRenderValue={false}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    styles={selectMenuStyles}
                                />
                            </Form.Group>

                            <Form.Group controlId="function" className="col-6">
                                <Form.Label>Function :</Form.Label>
                                <CustomSelect
                                    isMulti
                                    name="function"
                                    id="function"
                                    options={fnOptions}
                                    onChange={(selected) => handleMultiFilterChange(selected, fnOptions, onFunctionFilterChange)}
                                    placeholder={selectedCountPlaceholder('Select Function', selectedFnOptions)}
                                    classNamePrefix="select-control"
                                    components={{ Option }}
                                    allowSelectAll={true}
                                    allOption={ALL_FILTER_OPTION}
                                    value={withAllOptionWhenFullySelected(selectedFnOptions, fnOptions)}
                                    isClearable={false}
                                    controlShouldRenderValue={false}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    styles={selectMenuStyles}
                                />
                            </Form.Group>

                            <Form.Group controlId="kpi" className="col-6">
                                <Form.Label>KPI :</Form.Label>
                                <CustomSelect
                                    isMulti
                                    name="kpi"
                                    id="kpi"
                                    options={kpiOptions}
                                    onChange={(selected) => handleMultiFilterChange(selected, kpiOptions, onKpiFilterChange)}
                                    placeholder={selectedCountPlaceholder('Select KPI', selectedKpiOptions)}
                                    classNamePrefix="select-control"
                                    components={{ Option }}
                                    allowSelectAll={true}
                                    allOption={ALL_FILTER_OPTION}
                                    value={withAllOptionWhenFullySelected(selectedKpiOptions, kpiOptions)}
                                    isClearable={false}
                                    controlShouldRenderValue={false}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    styles={selectMenuStyles}
                                />
                            </Form.Group>
                        </div>
                    </Form>

                    <div className="qd_search-rst-footer">
                        <ol className="qd_tab-separated-block">
                            <li className="tab-separated-value">
                                <span><strong>{businesses.length}</strong> Business</span>
                            </li>
                            <li className="tab-separated-value">
                                <span><strong>{functions.length}</strong> Function</span>
                            </li>
                            <li className="tab-separated-value">
                                <span><strong>{kpis.length}</strong> KPIs</span>
                            </li>
                        </ol>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DomainMappingSearchBar;
